import { useState, type ReactNode } from 'react'
import { ListMusic, TrendingUp, ShoppingCart, Users, LogOut, MonitorPlay, AudioLines, Server } from 'lucide-react'
import { bacaSesi, hapusSesi, type Sesi } from './lib/api'
import Masuk from './pages/Masuk'
import Katalog from './pages/Katalog'
import Chart from './pages/Chart'
import Pesanan from './pages/Pesanan'
import Pengguna from './pages/Pengguna'
import RoomPage from './pages/Room'
import { KatalogLuarStaf } from './pages/JelajahLuar'
import ServerOutlet from './pages/ServerOutlet'
// Logo di-IMPOR, bukan ditulis sebagai "/logo-el.png".
//
// Berkas di public/ disalin Vite apa adanya beserta mode-nya; yang bermode 600
// tidak terbaca nginx dan tampil sebagai ikon rusak — ADA tapi tidak terbaca,
// jadi memeriksa "tersalin atau tidak" tidak menemukan apa pun.
//
// Yang di-impor dikeluarkan Vite sendiri ke /assets/ dengan mode 644 dan nama
// ber-hash. Jalur yang sama dengan JS aplikasinya — yang jelas terbaca, karena
// kalau tidak, halaman ini tidak akan muncul sama sekali.
import logoEl from './assets/logo-el.png'

type Tab = 'room' | 'katalog' | 'luar' | 'chart' | 'pesanan' | 'server' | 'pengguna'

export default function App() {
  const [sesi, setSesi] = useState<Sesi | null>(bacaSesi())
  const [tab, setTab] = useState<Tab>('room')

  if (!sesi) return <Masuk selesai={setSesi} />

  const izin = sesi.izin
  const TABS: { id: Tab; label: string; icon: ReactNode; tampil: boolean }[] = [
    // Room lebih dulu — ini layar yang dibuka tiap malam. Chart dilihat
    // seminggu sekali; menaruhnya di depan berarti staf melewati satu tab
    // setiap kali membuka aplikasi.
    { id: 'room',     label: 'Room',             icon: <MonitorPlay size={15} />,  tampil: true },
    { id: 'chart',    label: 'Chart & Pantauan', icon: <TrendingUp size={15} />,   tampil: true },
    { id: 'pesanan',  label: 'Daftar Pesanan',   icon: <ShoppingCart size={15} />, tampil: true },
    { id: 'katalog',  label: 'Katalog Lagu',     icon: <ListMusic size={15} />,    tampil: true },
    // 3 Okt 2026: jelajah Spotify & YouTube (sama dengan remote tamu) →
    // lagu yang belum punya versi karaoke masuk Daftar Pesanan.
    { id: 'luar',     label: 'Spotify & YouTube', icon: <AudioLines size={15} />,  tampil: true },
    // 21 Okt 2026: PC cermin lagu per outlet (server lagu LAN) — hanya pengelola katalog.
    { id: 'server',   label: 'Server Outlet',    icon: <Server size={15} />,       tampil: izin.boleh_kelola_katalog },
    // Tab Pengguna hanya untuk yang berizin. Server tetap menolak tanpa izin —
    // ini supaya tab yang pasti kosong tidak ikut tampil.
    { id: 'pengguna', label: 'Pengguna',         icon: <Users size={15} />,        tampil: izin.boleh_kelola_user },
  ]

  return (
    <div className="min-h-screen">
      <header className="border-b border-border bg-bg-1/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center gap-3">
          <img src={logoEl} alt="El Group" width={32} height={32}
            className="w-8 h-8 rounded-lg object-contain bg-gold/10 border border-gold/25 p-0.5 shrink-0"
            onError={(e) => {
              // Cadangan huruf "el". Tanpa ini, gagal muat tampil sebagai ikon
              // rusak bawaan browser — yang terbaca sebagai aplikasi rusak,
              // bukan sebagai satu berkas gambar yang tidak terbaca.
              const el = e.currentTarget
              el.style.display = 'none'
              el.insertAdjacentHTML('afterend',
                '<div class="w-8 h-8 rounded-lg bg-gold/10 border border-gold/25 ' +
                'grid place-items-center text-gold text-[12px] font-semibold shrink-0">el</div>')
            }} />
          <div className="mr-2">
            <div className="text-[13px] font-semibold leading-tight">Karaoke</div>
            <div className="text-[10.5px] text-text-3 leading-tight">Katalog pusat</div>
          </div>

          <nav className="flex gap-1 overflow-x-auto">
            {TABS.filter(t => t.tampil).map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] whitespace-nowrap
                            border transition-colors ${
                  tab === t.id
                    ? 'bg-gold/10 text-gold border-gold/30'
                    : 'bg-transparent text-text-3 border-transparent hover:text-text-2'
                }`}>
                {t.icon} {t.label}
              </button>
            ))}
          </nav>

          <div className="flex-1" />

          <div className="text-right hidden sm:block">
            <div className="text-[12px] leading-tight">{sesi.user.nama}</div>
            {/* Izin ditampilkan supaya orang yang tidak melihat tombol kelola
                tahu SEBABNYA — tanpa ini, tombol yang hilang terbaca sebagai
                halaman yang rusak. */}
            <div className="text-[10.5px] text-text-3 leading-tight">
              {[izin.boleh_kelola_katalog && 'katalog',
                izin.boleh_putus_pengadaan && 'pengadaan',
                izin.boleh_kelola_user && 'pengguna'].filter(Boolean).join(' · ') || 'hanya lihat'}
            </div>
          </div>
          <button onClick={() => { hapusSesi(); setSesi(null) }} title="Keluar"
            className="text-text-3 hover:text-text p-1.5"><LogOut size={16} /></button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-6">
        {tab === 'room'     && <RoomPage />}
        {tab === 'chart'    && <Chart   bolehKelola={izin.boleh_kelola_katalog} />}
        {tab === 'pesanan'  && <Pesanan bolehKelola={izin.boleh_putus_pengadaan} />}
        {tab === 'katalog'  && <Katalog bolehKelola={izin.boleh_kelola_katalog} />}
        {tab === 'luar'     && <KatalogLuarStaf />}
        {tab === 'server'   && <ServerOutlet bolehKelola={izin.boleh_kelola_katalog} />}
        {tab === 'pengguna' && <Pengguna />}
      </main>
    </div>
  )
}
